import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton, Spinner } from '@/components/ui'
import { PAYMENT_METHODS } from '@/constants/payment'
import { useCancelOrder } from '@/hooks/features/orders'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

/** Cancels a whole sale on the device (offline too): every payment goes back by its own method, stock comes back. */
export const CancelDialog = ({ order, open, onOpenChange }: { order: PosModel.Order; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const cancel = useCancelOrder()
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  const confirm = () =>
    cancel.mutate(
      { id: order._id, reason: reason.trim() || undefined },
      {
        onSuccess: (o) => {
          toast.success(`${o.number} cancelled`, { description: 'Payments refunded, items back in stock.' })
          onOpenChange(false)
        },
        onError: (e) => toast.error(e.message),
      }
    )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Cancel {order.number}?</DialogTitle>
          <DialogDescription>The whole sale is cancelled. Hand back:</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-border-subtle rounded-md border border-border text-sm">
          {order.payments.map((p, i) => (
            <li key={i} className="flex justify-between px-3 py-2">
              <span className="text-text-2">{PAYMENT_METHODS.find((m) => m.id === p.method)?.label ?? p.method}</span>
              <span className="tabular-nums text-ink">{formatMoney(p.method === 'cash' ? p.amount - order.change : p.amount)}</span>
            </li>
          ))}
        </ul>
        <div className="space-y-1.5">
          <Label htmlFor="cancel-reason">Reason (optional)</Label>
          <Input id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Wrong items, customer changed mind…" />
        </div>
        <DialogFooter>
          <PosButton variant="secondary" onClick={() => onOpenChange(false)}>
            Keep sale
          </PosButton>
          <PosButton variant="destructive" onClick={confirm} disabled={cancel.isPending}>
            {cancel.isPending && <Spinner className="size-4" />} Cancel sale
          </PosButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
